import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  GET_MY_PROFILE,
  GET_MY_SUBSCRIPTION_STATUS,
  REMOVE_PROFILE_PHOTO,
  UPDATE_MY_PROFILE,
  UPLOAD_PROFILE_PHOTO,
} from './operations';
import { accountStrings } from '../../content/account';
import { getPublicErrorMessage } from '../../utils/errors';
import { apolloCacheKeyForAccount, clearPersistedApolloCache } from '../../services/apolloCachePersistence';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';
import {
  externalSharePreferenceOptions,
  resolveExternalSharingPreferences,
} from '../../content/shareHighlights';

const defaultPreferences = {
  measurementUnits: 'us',
  currency: 'USD',
  timeFormat: '12h',
  recommendationsEnabled: true,
  personalizationEnabled: true,
  defaultSharingPermission: 'view',
  allowEmailSharing: true,
};

const allowedPhotoTypes = ['image/jpeg', 'image/png', 'image/webp'];
const maxPhotoBytes = 2 * 1024 * 1024;
const currencyOptions = ['USD', 'CAD', 'EUR', 'GBP', 'INR'];

/**
 * Retrieves option label.
 *
 * @param {Array<*>} options - Behavioral options for the operation.
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function findOptionLabel(options, value) {
  return options.find((option) => option.value === value)?.label || value;
}

/**
 * Retrieves file as base64.
 *
 * @param {*} file - file value.
 * @returns {*} The derived operation result.
 */
function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = () => reject(new Error(accountStrings.messages.fileReadFailed));
    reader.readAsDataURL(file);
  });
}

/**
 * Retrieves preferences.
 *
 * @returns {*} The derived operation result.
 */
function loadPreferences() {
  try {
    return {
      ...defaultPreferences,
      ...JSON.parse(localStorage.getItem('hobtalk-preferences') || '{}'),
    };
  } catch {
    return defaultPreferences;
  }
}

/**
 * Retrieves premium hobbies.
 *
 * @param {*} subscription - subscription value.
 * @returns {*} The derived operation result.
 */
function getPremiumHobbies(subscription) {
  return subscription?.entitlements?.filter((item) => item.entitlement === 'premium') || [];
}

/**
 * Renders the account view experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.user - Authenticated user context.
 * @param {*} props.theme - Active application theme.
 * @param {*} props.onThemeChange - Callback invoked when theme change occurs.
 * @returns {JSX.Element} The rendered interface.
 */
export default function AccountView({ user, theme, onThemeChange }) {
  const { instance } = useMsal();
  const apolloClient = useApolloClient();
  const { data: profileData } = useQuery(GET_MY_PROFILE);
  const {
    data: subscriptionData,
    loading: isLoadingSubscription,
    error: subscriptionLoadError,
  } = useQuery(GET_MY_SUBSCRIPTION_STATUS);
  const [updateMyProfile, { loading: isSavingProfile }] = useMutation(UPDATE_MY_PROFILE, {
    refetchQueries: [{ query: GET_MY_PROFILE }],
  });
  const [uploadProfilePhoto, { loading: isUploadingPhoto }] = useMutation(UPLOAD_PROFILE_PHOTO, {
    refetchQueries: [{ query: GET_MY_PROFILE }],
  });
  const [removeProfilePhoto, { loading: isRemovingPhoto }] = useMutation(REMOVE_PROFILE_PHOTO, {
    refetchQueries: [{ query: GET_MY_PROFILE }],
  });
  const profile = profileData?.getMyProfile;
  const subscription = subscriptionData?.getMySubscriptionStatus;
  const displayName = profile?.displayName || user?.name || user?.username || accountStrings.defaults.displayName;
  const [preferences, setPreferences] = useState(loadPreferences);
  const [profileDraft, setProfileDraft] = useState({ displayName: '' });
  const [isEditingName, setIsEditingName] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [activeSetting, setActiveSetting] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [showUsageDetails, setShowUsageDetails] = useState(false);
  const [showPremiumUpsell, setShowPremiumUpsell] = useState(false);
  const [activeExternalShareContext, setActiveExternalShareContext] = useState(null);
  const [externalShareDraft, setExternalShareDraft] = useState(null);
  const isPremium = subscription?.entitlement === 'premium' && ['trialing', 'active'].includes(subscription?.status);
  const isBundle = isPremium && subscription?.planKind === 'bundle';
  const premiumHobbies = getPremiumHobbies(subscription);
  const recommendationsEnabled = profile?.recommendationsEnabled !== false;
  const defaultSharingPermission = profile?.defaultSharingPermission || preferences.defaultSharingPermission;
  const allowEmailSharing = profile?.shareByEmail ?? preferences.allowEmailSharing;
  const externalSharingPreferences = resolveExternalSharingPreferences(profile?.externalSharingPreferences);
  const usedHobbyCount = subscription?.limits?.filter((limit) => limit.used > 0).length || 0;
  const planDetailsSubtitle = isBundle
    ? accountStrings.subscription.bundleDetail
    : isPremium
      ? accountStrings.subscription.hobbyDetail(premiumHobbies.map((item) => item.label).join(', '))
    : subscription?.freeUsage
      ? accountStrings.subscription.freeEntryCount(subscription.freeUsage.used, subscription.freeUsage.limit)
      : `${usedHobbyCount} ${usedHobbyCount === 1 ? 'hobby' : 'hobbies'} used`;

  const settingOptions = {
    theme: {
      title: accountStrings.appearance.themeTitle,
      detail: accountStrings.appearance.themeDetail,
      value: theme,
      options: [
        { value: 'system', label: accountStrings.appearance.system },
        { value: 'dark', label: accountStrings.appearance.dark },
        { value: 'light', label: accountStrings.appearance.light },
      ],
      onSelect: onThemeChange,
    },
    measurementUnits: {
      title: accountStrings.preferences.measurementUnits,
      detail: accountStrings.preferences.measurementUnitsDetail,
      value: preferences.measurementUnits,
      options: [
        { value: 'us', label: accountStrings.preferences.unitOptions.us },
        { value: 'metric', label: accountStrings.preferences.unitOptions.metric },
      ],
      onSelect: (value) => updatePreference('measurementUnits', value),
    },
    currency: {
      title: accountStrings.preferences.currency,
      detail: accountStrings.preferences.currencyDetail,
      value: preferences.currency,
      options: currencyOptions.map((value) => ({ value, label: value })),
      onSelect: (value) => updatePreference('currency', value),
    },
    timeFormat: {
      title: accountStrings.preferences.timeFormat,
      detail: accountStrings.preferences.timeFormatDetail,
      value: preferences.timeFormat,
      options: [
        { value: '12h', label: accountStrings.preferences.timeOptions.twelveHour },
        { value: '24h', label: accountStrings.preferences.timeOptions.twentyFourHour },
      ],
      onSelect: (value) => updatePreference('timeFormat', value),
    },
    defaultSharingPermission: {
      title: accountStrings.privacy.defaultPermission,
      detail: accountStrings.privacy.defaultPermissionDetail,
      value: defaultSharingPermission,
      options: [
        { value: 'view', label: accountStrings.privacy.permissionOptions.view },
        { value: 'edit', label: accountStrings.privacy.permissionOptions.edit },
      ],
      onSelect: (value) => updateSharingPreference('defaultSharingPermission', value),
    },
  };

  const activeSettingConfig = activeSetting ? settingOptions[activeSetting] : null;

  useEffect(() => {
    setProfileDraft({
      displayName: profile?.displayName || displayName,
    });
  }, [displayName, profile?.displayName]);

  useEffect(() => {
    if (!profile) return;
    setPreferences((current) => ({
      ...current,
      defaultSharingPermission: profile.defaultSharingPermission || current.defaultSharingPermission,
      allowEmailSharing: profile.shareByEmail ?? current.allowEmailSharing,
    }));
  }, [profile]);

  useEffect(() => {
    localStorage.setItem('hobtalk-preferences', JSON.stringify(preferences));
  }, [preferences]);

  /**
   * Updates preference.
   *
   * @param {string} key - key value.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updatePreference = (key, value) => {
    setPreferences((current) => ({ ...current, [key]: value }));
  };

  /**
   * Updates sharing preference.
   *
   * @param {string} key - key value.
   * @param {*} value - Value to inspect or transform.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const updateSharingPreference = async (key, value) => {
    updatePreference(key, value);
    setProfileError('');
    try {
      await updateMyProfile({
        variables: {
          input: key === 'defaultSharingPermission'
            ? { defaultSharingPermission: value }
            : { shareByEmail: Boolean(value) },
        },
      });
    } catch (error) {
      setProfileError(getPublicErrorMessage(error, accountStrings.messages.displayNameSaveFailed));
    }
  };

  /**
   * Handles recommendations enabled change.
   *
   * @param {boolean} enabled - Whether enabled is enabled.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleRecommendationsEnabledChange = async (enabled) => {
    setProfileError('');
    try {
      await updateMyProfile({
        variables: {
          input: {
            recommendationsEnabled: enabled,
          },
        },
      });
    } catch (error) {
      setProfileError(getPublicErrorMessage(error, accountStrings.messages.displayNameSaveFailed));
    }
  };

  /**
   * Handles logout.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleLogout = async () => {
    await apolloClient.clearStore().catch(() => {});
    await clearPersistedApolloCache(apolloCacheKeyForAccount(user));
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  /**
   * Handles save display name.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSaveDisplayName = async () => {
    const nextName = profileDraft.displayName.trim();
    setProfileError('');
    if (!nextName) {
      setProfileError(accountStrings.messages.displayNameRequired);
      return;
    }

    try {
      await updateMyProfile({
        variables: {
          input: {
            displayName: nextName,
          },
        },
      });
      setIsEditingName(false);
    } catch (error) {
      setProfileError(getPublicErrorMessage(error, accountStrings.messages.displayNameSaveFailed));
    }
  };

  /**
   * Handles cancel display name.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleCancelDisplayName = () => {
    setProfileDraft({ displayName });
    setIsEditingName(false);
    setProfileError('');
  };

  /**
   * Handles name key down.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleNameKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSaveDisplayName();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelDisplayName();
    }
  };

  /**
   * Handles photo upload.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setPhotoError('');
    if (!file) return;

    if (!allowedPhotoTypes.includes(file.type)) {
      setPhotoError(accountStrings.messages.unsupportedPhotoType);
      return;
    }

    if (file.size > maxPhotoBytes) {
      setPhotoError(accountStrings.messages.photoTooLarge);
      return;
    }

    try {
      const base64Data = await readFileAsBase64(file);
      await uploadProfilePhoto({
        variables: {
          input: {
            contentType: file.type,
            base64Data,
          },
        },
      });
      setShowPhotoOptions(false);
    } catch (error) {
      setPhotoError(getPublicErrorMessage(error, accountStrings.messages.photoUploadFailed));
    }
  };

  /**
   * Handles remove photo.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleRemovePhoto = async () => {
    setPhotoError('');
    try {
      await removeProfilePhoto();
      setShowPhotoOptions(false);
    } catch (error) {
      setPhotoError(getPublicErrorMessage(error, accountStrings.messages.photoRemoveFailed));
    }
  };

  /**
   * Handles select setting.
   *
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleSelectSetting = (value) => {
    activeSettingConfig?.onSelect(value);
    setActiveSetting(null);
  };

  /**
   * Performs external share settings.
   *
   * @param {*} context - context value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const openExternalShareSettings = (context) => {
    setExternalShareDraft({ ...externalSharingPreferences[context] });
    setActiveExternalShareContext(context);
  };

  /**
   * Performs external share settings.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const saveExternalShareSettings = async () => {
    if (!activeExternalShareContext || !externalShareDraft) return;
    setProfileError('');
    try {
      await updateMyProfile({
        variables: {
          input: {
            externalSharingPreferences: {
              [activeExternalShareContext]: externalShareDraft,
            },
          },
        },
      });
      setActiveExternalShareContext(null);
      setExternalShareDraft(null);
    } catch (error) {
      setProfileError(getPublicErrorMessage(error, accountStrings.messages.displayNameSaveFailed));
    }
  };

  /**
   * Renders usage limit.
   *
   * @param {number} limit - limit numeric value.
   * @returns {JSX.Element} The derived operation result.
   */
  const renderUsageLimit = (limit) => {
    const label = limit.hobby === 'all'
      ? accountStrings.subscription.sharedEntries
      : accountStrings.subscription.hobbyNames[limit.hobby] || limit.hobby;
    const percent = limit.limit ? Math.min(100, Math.round((limit.used / limit.limit) * 100)) : 100;
    const value = limit.limit == null
      ? accountStrings.subscription.usageUnlimited
      : `${limit.used}/${limit.limit}`;
    const helper = limit.limit == null
      ? accountStrings.subscription.usageUnlimited
      : `${limit.used} ${accountStrings.subscription.usageUsed} - ${limit.remaining} ${accountStrings.subscription.usageRemaining}`;
    return (
      <div className="usage-limit-row" key={limit.hobby}>
        <div className="usage-limit-topline">
          <strong>{label}</strong>
          <span>{value}</span>
        </div>
        <div className="usage-limit-meter" aria-hidden="true">
          <span style={{ width: `${percent}%` }} />
        </div>
        <small>{helper}</small>
      </div>
    );
  };

  /**
   * Renders usage details modal.
   *
   * @returns {JSX.Element} The derived operation result.
   */
  const renderUsageDetailsModal = () => {
    if (!subscription || !showUsageDetails) return null;
    return (
      <div className="modal-overlay" role="presentation" onMouseDown={() => setShowUsageDetails(false)}>
        <div className="modal-content usage-details-modal" role="dialog" aria-modal="true" aria-labelledby="usage-details-title" onMouseDown={(event) => event.stopPropagation()}>
          <div className="settings-choice-header">
            <div>
              <p className="eyebrow">{accountStrings.subscription.title}</p>
              <h3 id="usage-details-title">{accountStrings.subscription.planLimitsTitle}</h3>
              <p className="usage-details-copy">{isPremium ? accountStrings.subscription.premiumBenefitsDetail : accountStrings.subscription.usageDetail}</p>
            </div>
            <button type="button" className="icon-action" onClick={() => setShowUsageDetails(false)} aria-label={accountStrings.settingChooser.close}>×</button>
          </div>

          {isBundle && (
            <div className="subscription-benefits-panel compact">
              <h4>{accountStrings.subscription.allUnlockedTitle}</h4>
              <p className="usage-details-copy">{accountStrings.subscription.bundleDetail}</p>
            </div>
          )}

          {!isBundle && premiumHobbies.length > 0 && (
            <div className="subscription-benefits-panel compact">
              <h4>{accountStrings.subscription.premiumHobbiesTitle}</h4>
              <div className="subscription-benefits">
                {premiumHobbies.map((hobby) => <span key={hobby.hobby}>{hobby.label}</span>)}
              </div>
            </div>
          )}

          {!isBundle && (
            <div className="usage-limit-grid">
              {subscription.freeUsage && renderUsageLimit({ hobby: 'all', ...subscription.freeUsage })}
            </div>
          )}
        </div>
      </div>
    );
  };

  /**
   * Renders setting row.
   *
   * @param {string} settingKey - setting key value.
   * @returns {JSX.Element} The derived operation result.
   */
  const renderSettingRow = (settingKey) => {
    const config = settingOptions[settingKey];
    return (
      <button type="button" className="settings-row settings-option-row" onClick={() => setActiveSetting(settingKey)}>
        <div>
          <h4>{config.title}</h4>
          <p>{config.detail}</p>
        </div>
        <span className="settings-option-value">
          {findOptionLabel(config.options, config.value)}
          <span aria-hidden="true">›</span>
        </span>
      </button>
    );
  };

  return (
    <div className="account-page">
      <section className="account-hero">
        <div className="account-profile-form">
          <div className="profile-avatar-control">
            <button
              type="button"
              className="account-avatar profile editable"
              disabled={isUploadingPhoto || isRemovingPhoto}
              onClick={() => {
                setShowPhotoOptions(true);
                setPhotoError('');
              }}
              aria-label={accountStrings.profile.changePhoto}
            >
              {profile?.photoUrl ? <img src={profile.photoUrl} alt="" /> : displayName.charAt(0).toUpperCase()}
              <span>{isUploadingPhoto ? accountStrings.profile.updatingPhoto : accountStrings.profile.changePhoto}</span>
            </button>
          </div>

          <div className="profile-editor-fields">
            <p className="eyebrow">{accountStrings.profile.eyebrow}</p>
            <div className="display-name-readonly">
              <h1>{displayName}</h1>
            </div>
            <button
              type="button"
              className="btn-secondary profile-edit-button"
              onClick={() => {
                setProfileDraft({ displayName });
                setIsEditingName(true);
                setProfileError('');
              }}
            >
              {accountStrings.profile.editProfile}
            </button>
            {isSavingProfile && <p className="account-support-copy">{accountStrings.profile.saving}</p>}
            {!isEditingName && profileError && <p className="error-copy">{profileError}</p>}
            {!showPhotoOptions && photoError && <p className="error-copy">{photoError}</p>}
          </div>
        </div>
      </section>

      <section className="settings-panel subscription-panel">
        <div className="section-heading">
          <div>
            <h3>{accountStrings.subscription.title}</h3>
          </div>
        </div>

        {isLoadingSubscription && !subscription && (
          <div className="subscription-skeleton" aria-label={accountStrings.subscription.loading}>
            <div className="subscription-skeleton-row">
              <div>
                <span className="skeleton-line short" />
                <span className="skeleton-line medium" />
              </div>
              <span className="skeleton-pill" />
            </div>
            <div className="subscription-skeleton-divider" />
            <div className="subscription-skeleton-row">
              <div>
                <span className="skeleton-line compact" />
                <span className="skeleton-line long" />
              </div>
              <span className="skeleton-line action" />
            </div>
          </div>
        )}

        {subscription && (
          <div className="subscription-stack">
            <button type="button" className="settings-row settings-option-row subscription-current-row" onClick={() => setShowPremiumUpsell(true)}>
              <div>
                <h4>{accountStrings.subscription.currentPlanLabel}</h4>
                <p>{isBundle ? accountStrings.subscription.bundleDetail : isPremium ? premiumHobbies.map((item) => item.label).join(', ') : accountStrings.subscription.freeDetail}</p>
                {subscription.pendingChange && <p>{accountStrings.subscription.upsell.pendingChange}</p>}
              </div>
              <span className={`settings-option-value plan-value ${isPremium ? 'premium' : ''}`}>{isPremium ? accountStrings.subscription.premiumName : accountStrings.subscription.freeName}<span aria-hidden="true">›</span></span>
            </button>

            <button type="button" className="subscription-limit-row settings-option-row" onClick={() => setShowUsageDetails(true)}>
              <div>
                <h4>{accountStrings.subscription.planLimitsTitle}</h4>
                <p>{planDetailsSubtitle}</p>
              </div>
              <span className="settings-option-value">
                {accountStrings.subscription.limitLabel}
                <span aria-hidden="true">›</span>
              </span>
            </button>

            {subscriptionError && <p className="error-copy">{subscriptionError}</p>}
          </div>
        )}
      </section>

      <section className="settings-panel">
        <div className="section-heading">
          <div>
            <h3>{accountStrings.appearance.title}</h3>
          </div>
        </div>

        <div className="settings-stack">
          {renderSettingRow('theme')}
        </div>
      </section>

      <section className="settings-panel">
        <div className="section-heading">
          <div>
            <h3>{accountStrings.recommendations.title}</h3>
          </div>
        </div>

        <div className="settings-stack">
          <label className="settings-row checkbox-setting">
            <div>
              <h4>{accountStrings.recommendations.personalTitle}</h4>
              <p>{accountStrings.recommendations.personalDetail}</p>
            </div>
            <input
              type="checkbox"
              checked={recommendationsEnabled}
              onChange={(event) => handleRecommendationsEnabledChange(event.target.checked)}
            />
          </label>
        </div>
      </section>

      <section className="settings-panel">
        <div className="section-heading">
          <div>
            <h3>{accountStrings.privacy.title}</h3>
          </div>
        </div>

        <div className="settings-stack">
          <div className="settings-group-label">{accountStrings.privacy.sharedAccess}</div>
          {renderSettingRow('defaultSharingPermission')}

          <label className="settings-row checkbox-setting">
            <div>
              <h4>{accountStrings.privacy.allowEmailSharing}</h4>
              <p>{accountStrings.privacy.allowEmailSharingDetail}</p>
            </div>
            <input
              type="checkbox"
              checked={allowEmailSharing}
              onChange={(event) => updateSharingPreference('allowEmailSharing', event.target.checked)}
            />
          </label>

          <div className="settings-group-label">{accountStrings.privacy.publicSharing}</div>
          <button type="button" className="settings-row settings-option-row" onClick={() => openExternalShareSettings('shareHighlights')}>
            <div><h4>{accountStrings.privacy.shareHighlights}</h4></div>
            <span className="settings-option-value" aria-hidden="true">›</span>
          </button>
          <button type="button" className="settings-row settings-option-row" onClick={() => openExternalShareSettings('insightRecaps')}>
            <div><h4>{accountStrings.privacy.insightRecaps}</h4></div>
            <span className="settings-option-value" aria-hidden="true">›</span>
          </button>
        </div>
      </section>

      <section className="settings-panel">
        <div className="settings-row">
          <div>
            <h4>{accountStrings.logout.title}</h4>
            <p>{accountStrings.logout.detail}</p>
          </div>

          <button type="button" className="btn-secondary" onClick={handleLogout}>
            {accountStrings.logout.action}
          </button>
        </div>
      </section>

      {activeSettingConfig && (
        <div className="modal-overlay" role="presentation" onMouseDown={() => setActiveSetting(null)}>
          <div className="modal-content settings-choice-modal" role="dialog" aria-modal="true" aria-labelledby="settings-choice-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="settings-choice-header">
              <div>
                <p className="eyebrow">{activeSettingConfig.title}</p>
                <h3 id="settings-choice-title">{activeSettingConfig.title}</h3>
              </div>
              <button type="button" className="icon-action" onClick={() => setActiveSetting(null)} aria-label={accountStrings.settingChooser.close}>×</button>
            </div>
            <div className="settings-choice-list">
              {activeSettingConfig.options.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={`settings-choice ${activeSettingConfig.value === option.value ? 'active' : ''}`}
                  onClick={() => handleSelectSetting(option.value)}
                >
                  <span>{option.label}</span>
                  {activeSettingConfig.value === option.value && <strong aria-hidden="true">✓</strong>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {activeExternalShareContext && externalShareDraft && (
        <div className="modal-overlay" role="presentation" onMouseDown={() => setActiveExternalShareContext(null)}>
          <div className="modal-content settings-choice-modal public-sharing-modal" role="dialog" aria-modal="true" aria-labelledby="public-sharing-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="settings-choice-header">
              <div>
                <p className="eyebrow">{accountStrings.privacy.publicSharing}</p>
                <h3 id="public-sharing-title">{activeExternalShareContext === 'shareHighlights' ? accountStrings.privacy.shareHighlights : accountStrings.privacy.insightRecaps}</h3>
              </div>
              <button type="button" className="icon-action" onClick={() => setActiveExternalShareContext(null)} aria-label={accountStrings.settingChooser.close}>×</button>
            </div>
            <div className="public-sharing-options">
              {externalSharePreferenceOptions.map((option) => (
                <label className="settings-row checkbox-setting" key={option.key}>
                  <span>{option.label}</span>
                  <input type="checkbox" checked={Boolean(externalShareDraft[option.key])} onChange={(event) => setExternalShareDraft((current) => ({ ...current, [option.key]: event.target.checked }))} />
                </label>
              ))}
            </div>
            <p className="muted-copy">{accountStrings.privacy.neverShared}</p>
            <button type="button" className="btn-primary" disabled={isSavingProfile} onClick={saveExternalShareSettings}>{accountStrings.privacy.saveDefaults}</button>
          </div>
        </div>
      )}
      {isEditingName && (
        <div className="modal-overlay" role="presentation" onMouseDown={handleCancelDisplayName}>
          <div className="modal-content settings-choice-modal profile-edit-modal" role="dialog" aria-modal="true" aria-labelledby="profile-edit-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="settings-choice-header">
              <div>
                <p className="eyebrow">{accountStrings.profile.eyebrow}</p>
                <h3 id="profile-edit-title">{accountStrings.profile.editProfile}</h3>
              </div>
              <button type="button" className="icon-action" onClick={handleCancelDisplayName} aria-label={accountStrings.settingChooser.close}>×</button>
            </div>
            <div className="settings-form">
              <div className="form-group">
                <label htmlFor="account-display-name">{accountStrings.profile.displayNameLabel}</label>
                <input
                  id="account-display-name"
                  value={profileDraft.displayName}
                  placeholder={accountStrings.profile.displayNamePlaceholder}
                  onChange={(event) => setProfileDraft((current) => ({ ...current, displayName: event.target.value }))}
                  onKeyDown={handleNameKeyDown}
                  aria-label={accountStrings.profile.displayNameLabel}
                  autoFocus
                />
              </div>
              {profileError && <p className="error-copy">{profileError}</p>}
              <div className="form-actions">
                <button type="button" className="btn-secondary" disabled={isSavingProfile} onClick={handleCancelDisplayName}>
                  {accountStrings.profile.cancel}
                </button>
                <button type="button" className="btn-primary" disabled={isSavingProfile} onClick={handleSaveDisplayName}>
                  {isSavingProfile ? accountStrings.profile.saving : accountStrings.profile.saveProfile}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPhotoOptions && (
        <div className="modal-overlay" role="presentation" onMouseDown={() => setShowPhotoOptions(false)}>
          <div className="modal-content settings-choice-modal photo-options-modal" role="dialog" aria-modal="true" aria-labelledby="photo-options-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="settings-choice-header">
              <div>
                <p className="eyebrow">{accountStrings.profile.eyebrow}</p>
                <h3 id="photo-options-title">{accountStrings.profile.changePhoto}</h3>
              </div>
              <button type="button" className="icon-action" onClick={() => setShowPhotoOptions(false)} aria-label={accountStrings.settingChooser.close}>×</button>
            </div>
            <div className="settings-choice-list">
              <label className="settings-choice photo-file-choice">
                <span>{accountStrings.profile.choosePhoto}</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={isUploadingPhoto || isRemovingPhoto}
                  onChange={handlePhotoUpload}
                />
              </label>
              <label className="settings-choice photo-file-choice">
                <span>{accountStrings.profile.takePhoto}</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="user"
                  disabled={isUploadingPhoto || isRemovingPhoto}
                  onChange={handlePhotoUpload}
                />
              </label>
              {profile?.photoUrl && (
                <button
                  type="button"
                  className="settings-choice danger-choice"
                  disabled={isUploadingPhoto || isRemovingPhoto}
                  onClick={handleRemovePhoto}
                >
                  <span>{isRemovingPhoto ? accountStrings.profile.removingPhoto : accountStrings.profile.removePhoto}</span>
                </button>
              )}
            </div>
            {(isUploadingPhoto || isRemovingPhoto) && <p className="account-support-copy">{isUploadingPhoto ? accountStrings.profile.updatingPhoto : accountStrings.profile.removingPhoto}</p>}
            {photoError && <p className="error-copy">{photoError}</p>}
          </div>
        </div>
      )}
      {renderUsageDetailsModal()}
      {showPremiumUpsell && (
        <PremiumUpsellModal subscription={subscription} onClose={() => setShowPremiumUpsell(false)} />
      )}
    </div>
  );
}
